import { useState } from "react";
import { Row, Col, Card } from "react-bootstrap";
import { ColumnDef } from "@tanstack/react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileExport } from "@fortawesome/pro-light-svg-icons";
import DataTable from "@components/DataTable/DataTable";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { snakeToText } from "@utils/helperFunctions";
import { useListDocumentDeliveries } from "./api";
import { DocumentDelivery } from "./types";
import { useStyles } from "./styles";

const DocumentDeliveries = (): JSX.Element => {
  const styles = useStyles();
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const { deliveries, pageMeta, isLoading } = useListDocumentDeliveries({
    page: { number: pageNumber, size: pageSize },
  });

  const columns: ColumnDef<DocumentDelivery>[] = [
    {
      header: "Project",
      cell: (props) => props.row.original.projectName,
    },
    {
      header: "Document",
      cell: (props) => props.row.original.documentName,
    },
    {
      header: "Delivered By",
      cell: (props) => snakeToText(props.row.original.deliveryType),
    },
    {
      header: "Delivered To",
      cell: (props) => props.row.original.recipients,
    },
    {
      header: "Status",
      cell: (props) => (
        <>
          {snakeToText(props.row.original.status)}
          {props.row.original.errorInfo && (
            <span className={styles.errorInfo}>
              {props.row.original.errorInfo}
            </span>
          )}
        </>
      ),
    },
  ];

  return (
    <div className={styles.wrapper}>
      <Row>
        <Col>
          <h1 className={styles.title}>Document Deliveries</h1>
        </Col>
        <hr className={styles.line} />
      </Row>
      <Row>
        <Col>
          <Card className={styles.card}>
            <div className={styles.cardHeader}>
              <div className={styles.cardTitle}>
                <FontAwesomeIcon
                  className={styles.cardIcon}
                  icon={faFileExport}
                />
                <h2>Recent Deliveries</h2>
              </div>
            </div>
            <hr className={styles.line} />
            {isLoading ? (
              <ScopeStackSpinner />
            ) : deliveries.length === 0 ? (
              <div className={styles.emptyState}>
                <h3>No document deliveries yet</h3>
                <p>Documents you deliver from a project will appear here.</p>
              </div>
            ) : (
              <DataTable
                data={deliveries}
                columns={columns}
                striped
                bordered
                totalPages={pageMeta.pageCount}
                totalRows={pageMeta.recordCount}
                currentPage={pageNumber}
                setCurrentPage={setPageNumber}
                pageSize={pageSize}
                onPageSizeChange={setPageSize}
                paginationEnabled
                pageSizeEnabled
              />
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default DocumentDeliveries;
